<div class="new-recipe-dialog">
  @let title =
    _recipeDetails?.recipeName
      ? (_recipeDetails?.title ?? "")
      : "createANewRecipe";
  <span mat-dialog-title class="dialog-title">{{ title | translate }}</span>
  <button mat-icon-button class="close-icon" (click)="closeDialog()">
    <span class="material-symbols-outlined">close</span>
  </button>
  <mat-dialog-content #dialogContent class="new-recipe-dialog-content">
    <!-- Option to link Recipe to an existing Project -->
    <div class="new-recipe-header display-align">
      <span
        >{{ "newRecipeDialog_recipeTiedToProject" | translate }} &nbsp;</span
      >
      <div class="display-align gap-margin">
        <span>{{ controls.isTiedToProject.value ? "Yes" : "No" }}</span>
        <mat-slide-toggle
          [checked]="controls.isTiedToProject.value"
          (change)="toggleIsTiedToProject()"
          color="primary"
        />
      </div>
    </div>
    <span class="subtitle-text">{{
      "newRecipeDialog_createRecipeInternal" | translate
    }}</span>
    <br /><br />
    <!-- Project Search field -->
    @if (controls.isTiedToProject.value) {
      <div>
        <h4 class="new-recipe-header margin-top-bottom">
          {{ "projectOpportunity" | translate }}*
        </h4>
        <mat-form-field appearance="outline">
          <span class="material-symbols-outlined" matPrefix>search</span>
          <input
            type="text"
            matInput
            [matAutocomplete]="autoProject"
            [formControl]="controls.projectSearch"
            [placeholder]="'projectSearchPlaceholder' | translate"
            (keydown.enter)="searchProjects()"
          />
          <span
            matSuffix
            class="material-symbols-outlined search-trigger-icon"
            (click)="searchProjects()"
            [ngClass]="{ 'search-disabled': !controls.projectSearch.value }"
            tabindex="0"
            role="button"
          >
            subdirectory_arrow_left
          </span>
          @if (
            controls.projectSearch.invalid &&
            (controls.projectSearch.touched || showErrors)
          ) {
            <mat-error>{{ "requiredProject" | translate }}</mat-error>
          }
          <mat-autocomplete
            #autoProject="matAutocomplete"
            (optionSelected)="onProjectSelected($event)"
          >
            @for (project of filteredProjects(); track project.accountId) {
              <mat-option [value]="project.name">
                {{ project.name }}
              </mat-option>
            }
            @if (hasSearchedProjects() && filteredProjects().length === 0) {
              <mat-option disabled> No projects found </mat-option>
            }
          </mat-autocomplete>
        </mat-form-field>
      </div>
    }
    <!-- Recipe Name Input field -->
    <div class="flex">
      <h4 class="new-recipe-header margin-top-bottom">
        {{ "recipeName" | translate }}*
      </h4>
      <span
        class="material-symbols-outlined info-icon margin-top-bottom"
        (click)="controls.showInfo.setValue(!controls.showInfo.value)"
        tabindex="0"
        role="button"
        >info</span
      >
      @if (controls.showInfo.value) {
        <span class="subtitle-convention-text margin-top-bottom">
          {{ "newRecipeDialog_namingConvention" | translate }}
        </span>
      }
    </div>
    <mat-form-field appearance="outline">
      <input matInput [formControl]="controls.recipeName" required />
      @if (
        controls.recipeName.invalid &&
        (controls.recipeName.touched || showErrors)
      ) {
        <mat-error>{{ "requiredRecipeName" | translate }}</mat-error>
      }
    </mat-form-field>
    <!-- Region Selector -->
    <h4 class="new-recipe-header margin-top-bottom">
      {{ "region" | translate }}*
    </h4>
    <mat-form-field appearance="outline">
      <mat-select
        [formControl]="controls.region"
        (selectionChange)="setRegion($event.value)"
        [placeholder]="'regionPlaceholder' | translate"
        required
      >
        @for (region of regions; track region.name) {
          <mat-option [value]="region.id">
            {{ region.name }}
          </mat-option>
        }
      </mat-select>
      @if (controls.region.invalid && (controls.region.touched || showErrors)) {
        <mat-error>{{ "requiredRegion" | translate }}</mat-error>
      }
    </mat-form-field>
    <!-- Country Selector -->
    <h4 class="new-recipe-header margin-top-bottom">
      {{ "country" | translate }}*
    </h4>
    <mat-form-field appearance="outline">
      <mat-select
        [formControl]="controls.country"
        [placeholder]="'countryPlaceholder' | translate"
        required
      >
        @for (country of countries; track country.id) {
          <mat-option [value]="country.id">
            {{ country.name }}
          </mat-option>
        }
      </mat-select>
      @if (
        controls.country.invalid && (controls.country.touched || showErrors)
      ) {
        <mat-error>{{ "requiredCountry" | translate }}</mat-error>
      }
    </mat-form-field>
    <!-- Category Selector -->
    <h4 class="new-recipe-header margin-top-bottom">
      {{ "category" | translate }}*
    </h4>
    <mat-form-field appearance="outline">
      <mat-select
        [formControl]="controls.category"
        [placeholder]="'categoryPlaceholder' | translate"
        required
      >
        @for (category of categories; track category.name) {
          <mat-option
            [value]="category.id"
          >
            {{ category.name }}
          </mat-option>
        }
      </mat-select>
      @if (
        controls.category.invalid && (controls.category.touched || showErrors)
      ) {
        <mat-error>{{ "requiredCategory" | translate }}</mat-error>
      }
    </mat-form-field>
    <!-- Security related options -->
    <h4 class="new-recipe-header margin-top-bottom">
      {{ "security" | translate }}
    </h4>
    <div class="display-align">
      <span class="new-recipe-header">{{ "makePrivate" | translate }}</span>
      <div class="new-recipe-header display-align gap-margin">
        <span>{{ controls.isMadePrivate.value ? "Yes" : "No" }}</span>
        <mat-slide-toggle
          [checked]="controls.isMadePrivate.value"
          (change)="toggleIsMadePrivate()"
          color="primary"
        />
      </div>
    </div>
    <span class="subtitle-text">{{
      "newRecipeDialog_addUserToRecipeView" | translate
    }}</span>
    <br /><br />
    @if (controls.isMadePrivate.value) {
      <div class="flex">
        <div>
          <span class="new-recipe-header">{{
            "manageAccess" | translate
          }}</span>
          <br />
          <span class="subtitle-text">{{
            "newRecipeDialog_chooseView" | translate
          }}</span>
        </div>
        @if (!showPeoplePicker) {
          <button
            mat-flat-button
            class="add-viewer-button"
            (click)="showPeoplePicker = !showPeoplePicker; scrollToBottom()"
          >
            {{ "addViewers" | translate }}
          </button>
        }
        @if (showPeoplePicker) {
          <button
            mat-stroked-button
            class="add-viewer-button"
            (click)="showPeoplePicker = !showPeoplePicker"
          >
            {{ "cancel" | translate }}
          </button>
          <div class="show-people-picker-container">
            <app-people-picker
              [isQueryBuilder]="false"
              (selectedUserMailChange)="selectedPeoples($event)"
            />
          </div>
        }
      </div>
    }
  </mat-dialog-content>
  <mat-dialog-actions align="start" class="new-recipe-dialog-actions">
    <button mat-flat-button class="cancel-button" (click)="closeDialog()">
      {{ "cancel" | translate }}
    </button>
    <button
      mat-flat-button
      color="primary"
      class="create-button"
      (click)="createRecipe()"
    >
      {{
        controls.isTiedToProject.value
          ? ("createNewRecipe" | translate)
          : ("createInternalRecipe" | translate)
      }}
    </button>
  </mat-dialog-actions>
</div>
